import React from 'react';
import { Link } from 'react-router-dom';
import { Crest } from '../UniversityCrest';
import { FactSection } from './CollegeParts';
import { SCORECARD_NET_PRICE_YEAR, acceptanceRate, getCollegeBySlug, usd } from '../../data/colleges';
import { track, EVENTS } from '../../services/analytics';
import SIMILAR from '../../data/colleges/similar.json';

// "Similar schools" on every college page (#2895; founder pick A of the
// 2026-10-02 mocks, capped at 5 cards). The lists are built ahead of time by
// scripts/build_similar_schools.mjs from the college data files alone, and
// the build fails unless every page links 3 to 5 others and every page is
// linked from at least 3. Each card is a plain <a href> in the prerendered
// HTML: these links are how a crawler reaches a college page from another
// one, so they must never become click handlers or render only after load.

export const similarSchoolsFor = (slug) => (SIMILAR.lists[slug] || []).map(getCollegeBySlug).filter(Boolean);

export default function SimilarSchools({ college }) {
    const list = similarSchoolsFor(college.slug);
    if (!list.length) return null;
    const why = SIMILAR.why[college.slug]
        || `Chosen for how close each is to ${college.name} on acceptance rate, public or private, location and majors offered.`;
    return (
        <FactSection id="similar" title="Similar schools">
            <p>{why}</p>
            <ul data-testid="similar-schools" className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {list.map((s, i) => (
                    <li key={s.slug}>
                        <Link
                            to={`/colleges/${s.slug}`}
                            onClick={() => track(EVENTS.COLLEGE_SIMILAR_CLICKED, { slug: college.slug, to: s.slug, position: i + 1 })}
                            className="flex h-full min-h-[44px] items-start gap-3 rounded-xl border border-[#E0DED8] bg-white p-4 hover:border-[#1A4D2E] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#1A4D2E]"
                        >
                            <Crest name={s.name} branding={s.branding} size={40} />
                            <span className="min-w-0">
                                <span className="block font-semibold leading-snug text-[#1A2E1F] underline-offset-2 hover:underline">{s.name}</span>
                                <span className="block text-[13px] text-[#6B6B6B]">{s.location} · {s.control}</span>
                                <span className="mt-2 flex flex-wrap gap-x-4 gap-y-0.5 text-[13px] text-[#4A4A4A] tabular-nums">
                                    <span><strong className="text-[#1A2E1F]">{acceptanceRate(s)}%</strong> admitted</span>
                                    <span><strong className="text-[#1A2E1F]">{usd(s.cost.netPrice.average)}</strong> average net price</span>
                                </span>
                            </span>
                        </Link>
                    </li>
                ))}
            </ul>
            <p className="text-xs text-[#6B6B6B]">
                Admitted: each school's latest first-year acceptance rate, so the year can differ from school to school; each
                school's page names its year and source. Average net price: what federal aid recipients paid per year after
                grants, {SCORECARD_NET_PRICE_YEAR}, and the in-state figure for a public university.
            </p>
        </FactSection>
    );
}
